import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Rocket,
  BookOpen,
  FileText,
  Image,
  PlusCircle,
  Upload,
  Send,
  HelpCircle,
  X,
  ChevronRight,
  Clock,
  Sparkles
} from 'lucide-react';
import { Button } from './ui/button';

const API = '/api';

const OnboardingBanner = ({ className = '' }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/onboarding/creator-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching onboarding status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const trackCTAClick = async (ctaType) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/onboarding/track-event`, {
        event_type: 'cta_clicked',
        cta_type: ctaType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Silent fail
    }
  };

  const trackPublishFlowStart = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/onboarding/track-event`, {
        event_type: 'publish_flow_started'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Silent fail
    }
  };

  const handleAction = async (action, route) => {
    await trackCTAClick(action);
    if (action === 'publish_chapter' || action === 'review_publish') {
      await trackPublishFlowStart();
    }
    navigate(route);
  };

  if (dismissed || loading || !status) return null;
  if (status.activated) return null; // Don't show if already activated

  const { next_action, nudge_tier, content_counts, checklist } = status;

  // Time-based nudge banners (stronger nudges)
  if (nudge_tier === 'strong') {
    return (
      <div 
        className={`relative bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 rounded-xl p-4 ${className}`}
        data-testid="onboarding-banner-strong"
      >
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-3 right-3 text-zinc-500 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-purple-500/20">
            <HelpCircle className="w-6 h-6 text-purple-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white mb-1">Need Help Getting Started?</h3>
            <p className="text-zinc-400 text-sm mb-3">
              We noticed you haven't published yet. Check out our quick guide or let us help you create your first chapter!
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                className="bg-purple-500 hover:bg-purple-600 text-white"
                onClick={() => handleAction('view_guide', '/creator/guide')}
              >
                <BookOpen className="w-4 h-4 mr-1" />
                Quick Start Guide
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                onClick={() => handleAction('get_help', '/support')}
              >
                Get Help
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (nudge_tier === 'reminder') {
    return (
      <div 
        className={`relative bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-4 ${className}`}
        data-testid="onboarding-banner-reminder"
      >
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-3 right-3 text-zinc-500 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-center gap-4">
          <div className="p-2 rounded-lg bg-amber-500/20">
            <Clock className="w-5 h-5 text-amber-400" />
          </div>
          <div className="flex-1">
            <p className="text-amber-300 font-medium">
              Your creator journey awaits! Take the next step to share your work with the world.
            </p>
          </div>
          <Button
            size="sm"
            className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
            onClick={() => {
              if (!checklist.content_created) {
                handleAction('create_content', '/create-series');
              } else if (content_counts.draft_chapters > 0) {
                handleAction('review_publish', '/creator/dashboard');
              } else {
                handleAction('add_chapter', '/creator/dashboard');
              }
            }}
          >
            Continue
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  }

  // "Next Best Action" banner based on progress
  const renderNextActionBanner = () => {
    switch (next_action) {
      case 'create_first_content':
        return (
          <div 
            className={`bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-xl p-5 ${className}`}
            data-testid="onboarding-banner-create"
          >
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-orange-500/20">
                  <Rocket className="w-6 h-6 text-orange-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Start Your Creator Journey!</h3>
                  <p className="text-zinc-400 text-sm">Create your first series, novel, or artwork to begin</p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 w-full md:w-auto">
                <Button
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                  onClick={() => handleAction('create_series', '/create-series')}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Create Series
                </Button>
                <Button
                  variant="outline"
                  className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                  onClick={() => handleAction('create_novel', '/create-novel')}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Create Novel
                </Button>
                <Button
                  variant="outline"
                  className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                  onClick={() => handleAction('upload_artwork', '/art/upload')}
                >
                  <Image className="w-4 h-4 mr-2" />
                  Upload Art
                </Button>
              </div>
            </div>
          </div>
        );

      case 'upload_cover':
        return (
          <div 
            className={`bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-5 ${className}`}
            data-testid="onboarding-banner-cover"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-blue-500/20">
                  <Image className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Add a Cover Image</h3>
                  <p className="text-zinc-400 text-sm">Make your content stand out with a compelling cover</p>
                </div>
              </div>
              
              <Button
                className="bg-blue-500 hover:bg-blue-600 text-white"
                onClick={() => handleAction('upload_cover', '/creator/dashboard')}
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Cover
              </Button>
            </div>
          </div>
        );

      case 'add_chapter':
        return (
          <div 
            className={`bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-5 ${className}`}
            data-testid="onboarding-banner-chapter"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-green-500/20">
                  <PlusCircle className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Add Your First Chapter</h3>
                  <p className="text-zinc-400 text-sm">
                    {content_counts.series > 0 
                      ? "Your series is ready! Add a chapter to start publishing."
                      : "Your novel is ready! Add a chapter to share your story."}
                  </p>
                </div>
              </div>
              
              <Button
                className="bg-green-500 hover:bg-green-600 text-white"
                onClick={() => handleAction('add_chapter', '/creator/dashboard')}
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                Add Chapter
              </Button>
            </div>
          </div>
        );

      case 'publish_chapter':
        return (
          <div 
            className={`bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-5 ${className}`}
            data-testid="onboarding-banner-publish"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-purple-500/20 animate-pulse">
                  <Sparkles className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Ready to Publish!</h3>
                  <p className="text-zinc-400 text-sm">
                    You have {content_counts.draft_chapters} draft chapter{content_counts.draft_chapters !== 1 ? 's' : ''} ready to share with the world!
                  </p>
                </div>
              </div>
              
              <Button
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                onClick={() => handleAction('review_publish', '/creator/dashboard')}
              >
                <Send className="w-4 h-4 mr-2" />
                Review & Publish
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return renderNextActionBanner();
};

export default OnboardingBanner;
